"use client";

import { useSyncExternalStore } from "react";
import { EMPTY_LEARN_PROGRESS, LEARN_STORAGE_KEY, readLearnProgress, type LearnProgress } from "@/lib/learn";

let current = EMPTY_LEARN_PROGRESS;
let raw: string | null | undefined;
let volatile = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

function snapshot() {
  if (typeof window === "undefined" || volatile) return current;
  try {
    const stored = localStorage.getItem(LEARN_STORAGE_KEY);
    if (stored !== raw) {
      raw = stored;
      current = readLearnProgress(stored ? JSON.parse(stored) : null);
    }
  } catch { /* Keep the in-memory session available if storage is blocked or corrupt. */ }
  return current;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const sync = (event: StorageEvent) => { if (event.key === LEARN_STORAGE_KEY || event.key === null) { raw = undefined; notify(); } };
  window.addEventListener("storage", sync);
  return () => { listeners.delete(listener); window.removeEventListener("storage", sync); };
}

export function updateLearning(update: (value: LearnProgress) => LearnProgress) {
  current = readLearnProgress(update(snapshot()));
  try { raw = JSON.stringify(current); localStorage.setItem(LEARN_STORAGE_KEY, raw); volatile = false; }
  catch { volatile = true; }
  notify();
}

export function useLearning() {
  return useSyncExternalStore(subscribe, snapshot, () => EMPTY_LEARN_PROGRESS);
}

export const visitLesson = (id: string) => updateLearning((value) => ({ ...value, lastVisited: id }));
export const toggleSaved = (id: string) => updateLearning((value) => ({ ...value, saved: value.saved.includes(id) ? value.saved.filter((item) => item !== id) : [...value.saved, id] }));
export const toggleCompleted = (id: string) => updateLearning((value) => ({ ...value, completed: value.completed.includes(id) ? value.completed.filter((item) => item !== id) : [...value.completed, id] }));
